import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { isDefined } from '../../../../../../../utils/variables';
import { LineSegmentNode } from '../../../../../../../viewer/elements/design/design-node/utils/line-segment-node';
import { PointCloudHandle } from '../../../../../../../viewer/geodata/point-clouds/point-cloud-handle';
import { SubscriptionTopic } from '../../../../../../../viewer/messaging/pubsub';
import { ActionName, DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { offsetUtils } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/offset-utils';
import { listLinesAndPolygonsBySelection } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/multi-select';
import { loadHighResolutionLodPointCloudChunksFromRays } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/point-cloud';
import {
  deselect,
  selectLines,
} from '../../../../../../../viewer/tools/utils/context-menu/design-tool/utils/select';
import { useSubscribe } from '../../../../../../hooks/use-subscribe';
import { InfoBox } from '../../../../../info-box/info-box';
import {
  ProgressDialog,
  useProgressDialogDependencies,
} from '../../../../../progress-dialog/progress-dialog';
import { Stack } from '../../../../../stack/stack';
import { useCurrentSelection } from '../hooks/use-current-selection';

type Props = {
  tool: DesignTool;
};

const CreateOffset = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selection, setSelection] = React.useState<{
    isPolygon: boolean;
    lines: LineSegmentNode[];
  }>();
  const [actionInProgress, setActionInProgress] = React.useState(
    props.tool.actionInProgress?.actionName === ActionName.Offset,
  );

  useSubscribe(SubscriptionTopic.DesignToolActiveActionChanged, () => {
    setActionInProgress(props.tool.actionInProgress?.actionName === ActionName.Offset);
  });

  const { currentSelection } = useCurrentSelection(props.tool);
  const progress = useProgressDialogDependencies(3);

  const refresh = React.useCallback(() => {
    const autoTrigger = props.tool.autoTriggerOffset;
    props.tool.autoTriggerOffset = false;

    if (!isDefined(props.tool.actionInProgress)) {
      const result = listLinesAndPolygonsBySelection(props.tool.asset.designNode, [
        ...currentSelection.lineSegments,
      ]);
      const first = result.at(0);
      setSelection(first ?? undefined);

      if (isDefined(first) && autoTrigger) {
        offsetUtils.initCreateOffset(
          props.tool,
          first.lines.map((x) => x.id),
          progress.setShowDialog,
        );
      }
    }
  }, [props.tool, currentSelection, progress.setShowDialog]);

  React.useEffect(refresh, [refresh]);

  const onCreateOffsetTowardsPointCloud = async () => {
    if (!isDefined(selection)) {
      throw new Error(
        t('section.functions.offset.content.save.errorNoSelection', {
          ns: 'skyviewDesign',
        }) as string,
      );
    }

    const offsetConfiguration = offsetUtils.getOffsetConfiguration(
      props.tool,
      selection.lines.map((x) => x.id),
    );

    deselect(props.tool);
    selectLines(props.tool, offsetConfiguration.segments);

    if (!PointCloudHandle.instance.activePointCloud) {
      throw new Error(
        t('section.functions.offset.content.save.errorNoPointCloud', {
          ns: 'skyviewDesign',
        }) as string,
      );
    }

    try {
      const rays = offsetUtils.getOffsetRays(props.tool, offsetConfiguration);

      if (!isDefined(rays)) {
        return;
      }

      const pointCloudResult = await loadHighResolutionLodPointCloudChunksFromRays(
        rays,
        progress.controller.current,
        progress.abortController.current,
        PointCloudHandle.instance.activePointCloud,
      );

      if (progress.abortController.current.signal.aborted) {
        return;
      }

      progress.controller.current.callback(
        1,
        `${progress.controller.current.step} ${
          t('section.functions.offset.content.save.progressBar.text', {
            ns: 'skyviewDesign',
          }) as string
        }`,
      );

      // Generate once without high resolution pointcloud to get bounding box
      const result = offsetUtils.intersectProjectionResult(
        props.tool,
        rays,
        pointCloudResult.points,
        offsetConfiguration.isPolygon,
      );

      for (const child of pointCloudResult.parent.children) {
        child.removeFromParent();
      }

      if (!result) {
        throw new Error(
          t('section.functions.offset.content.save.errorUnknownError', {
            ns: 'skyviewDesign',
          }) as string,
        );
      }

      const localResult = result.map((x) => props.tool.asset.designNode.getLocalPosition(x));

      offsetUtils.applyOffsetResult(
        props.tool,
        localResult,
        offsetConfiguration.isPolygon,
        offsetUtils.getOffsetCurves(
          offsetConfiguration.positions,
          offsetConfiguration.curves,
          localResult,
        ),
      );
    } catch (err) {
      throw new Error(
        t('section.functions.offset.content.save.errorUnknownError', {
          ns: 'skyviewDesign',
        }) as string,
      );
    }
  };

  return (
    <Stack spacing={1}>
      {!isDefined(selection) ? (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
          leftTooltip={{
            title: t('section.functions.offset.content.infoNoSelection.tooltip.title', {
              ns: 'skyviewDesign',
            }),
            content: (
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="section.functions.offset.content.infoNoSelection.tooltip.text"
                ns="skyviewDesign"
              />
            ),
          }}
        >
          {t('section.functions.offset.content.infoNoSelection.text', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      ) : !actionInProgress ? (
        <InfoBox color="yellow">
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="section.functions.offset.content.infoSelectionNoAction"
            ns="skyviewDesign"
          />
        </InfoBox>
      ) : (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
          leftTooltip={{
            title: t('section.functions.offset.content.infoSelectionAction.tooltip.title', {
              ns: 'skyviewDesign',
            }),
            content: (
              <Trans
                components={{ bold: <strong /> }}
                i18nKey="section.functions.offset.content.infoSelectionAction.tooltip.text"
                ns="skyviewDesign"
              />
            ),
          }}
        >
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="section.functions.offset.content.infoSelectionAction.text"
            ns="skyviewDesign"
          />
        </InfoBox>
      )}

      {progress.showDialog && (
        <ProgressDialog
          abortController={progress.abortController.current}
          callback={onCreateOffsetTowardsPointCloud}
          progressController={progress.controller.current}
          title={t('section.functions.offset.content.save.progressBar.title', {
            ns: 'skyviewDesign',
          })}
          onClose={() => {
            progress.setShowDialog(false);
          }}
        />
      )}
    </Stack>
  );
};

export { CreateOffset };
